import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { InputBox, Button } from "../../Common";
import { addNewFollowup,editFollowup } from "../../api";
import styles from "./followup.module.css";
import {DropDown} from "../../Common";
import { CookieUtils } from "../../axios.config";

export const AddFollowup = ({ custId, onCancel, onSuccess, userName, isEditMode = false, followupData = null }) => {
  const [formData, setFormData] = useState({
    status: "",
    priority: "",
    followupsType: "",
    subject: "",
    description: "",
    platform: "investing",
    createdBy: "",
  });
  const [error, setError] = useState({});
  const [loader, setLoader] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const statusList = ["open", "in_progress", "resolved", "closed"];
  const priorityList = ["high", "medium", "low"];

  const initialFormState = {
    status: "open",
    priority: "low",
    followupsType: "",
    subject: "",
    description: "",
    platform: "investing",
    createdBy: "",
  };
  
  useEffect(() => {
    if (isEditMode && followupData) {
      setFormData({
        status: followupData.status || "",
        priority: followupData.priority || "",
        followupsType: followupData.followupsType || "",
        subject: followupData.subject || "",
        description: followupData.description || "",
        platform: followupData.platform || "investing",
        createdBy: followupData.createdBy || "",
      });
    } else {
      let createdByUser = userName;

      if (!createdByUser) {
        const cookieUserName = CookieUtils.get("username");
        if (cookieUserName) {
          try {
            createdByUser = JSON.parse(cookieUserName);
          } catch {
            createdByUser = cookieUserName;
          }
        }
      }

      setFormData({
        ...initialFormState,
        createdBy: createdByUser || "",
      });
    }
  }, [userName, isEditMode, followupData]);
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError({ ...error, [name]: "" });
  };

  const handleDropdownChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setError({ ...error, [field]: "" });
  };

  const fieldLabels = {
    status: "Status",
    priority: "Priority",
    followupsType: "Follow-up Type",
    subject: "Subject",
    description: "Description"
  };
  const handleValidation = () => {
    const newErrors = {};
    const requiredFields = [
      "status",
      "priority",
      "followupsType",
      "subject",
      "description",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${fieldLabels[field]} is required.`;
      }
    });

    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      ...initialFormState,
      createdBy: formData.createdBy,
    });
  };

  const handleSubmit = async () => {
    if (!handleValidation()) {
      return;
    }

    if (!formData.createdBy) {
      const cookieUserName = CookieUtils.get("username");
      if (cookieUserName) {
        try {
          const parsedUserName = JSON.parse(cookieUserName);
          setFormData((prev) => ({
            ...prev,
            createdBy: parsedUserName,
          }));
          return;
        } catch (e) {
          setFormData((prev) => ({
            ...prev,
            createdBy: cookieUserName,
          }));
          return;
        }
      }
    }

    setLoader(true);
    try {
      let response;

      if (isEditMode) {
        response = await editFollowup(followupData.followups_id, formData);
      } else {
        const payload = {
          ...formData,
          custId: custId,
        };
        response = await addNewFollowup(payload);
      }

      if (response?.data?.code === 200 || response?.code === 200) {
        setSuccessMessage(
          isEditMode
            ? "Follow-up updated successfully!"
            : "Follow-up created successfully!"
        );
        if (!isEditMode) {
          resetForm();
        }
        setTimeout(() => {
          setSuccessMessage("");
          onSuccess();
        }, 3000);
      } else {
        setError({
          form: response?.data?.message || "Failed to process follow-up.",
        });
      }
    } catch (err) {
      setError({
        form: `An error occurred while ${
          isEditMode ? "updating" : "adding"
        } the follow-up.`,
      });
    } finally {
      setLoader(false);
    }
  };


  return (
    <div className={styles.Container}>
      <div className={styles.followupFormContainer}>
        <h3>{isEditMode ? "Edit Follow-up" : "Add New Follow-up"}</h3>
        <form>
          <Row style={{ margin: "20px 0px" }}>
            <Col md={6} className={styles.inputDiv}>
              <DropDown
                label="Status"
                name="status"
                data={statusList.map((item) => ({ label: item, value: item }))}
                value={formData.status}
                onChange={(value) => handleDropdownChange("status", value)}
                error={!!error.status}
                helperText={error.status}
              />
            </Col>
            <Col md={6} className={styles.inputDiv}>
              <DropDown
                label="Priority"
                name="priority"
                data={priorityList.map((item) => ({ label: item, value: item }))}
                value={formData.priority}
                onChange={(value) => handleDropdownChange("priority", value)}
                error={!!error.priority}
                helperText={error.priority}
                disabled={isEditMode}
              />
            </Col>
          </Row>
          <Row style={{ margin: "20px 0px" }}>
            <Col md={6} className={styles.inputDiv}>
              <label>
                Follow-up Type <span className={styles.required}>*</span>
                <InputBox
                  placeholder="Enter Follow-up Type "
                  type="text"
                  name="followupsType"
                  onChange={handleChange}
                  value={formData.followupsType}
                  disabled={isEditMode}
                />
                {error.followupsType && (
                  <p className={styles.errorMessage}>{error.followupsType}</p>
                )}
              </label>
            </Col>
            <Col md={6} className={styles.inputDiv}>
              <label>
                Subject <span className={styles.required}>*</span>
                <InputBox
                  placeholder="Enter Subject"
                  type="text"
                  name="subject"
                  onChange={handleChange}
                  value={formData.subject}
                  disabled={isEditMode}
                />
                {error.subject && (
                  <p className={styles.errorMessage}>{error.subject}</p>
                )}
              </label>
            </Col>
          </Row>
          <Row style={{ margin: "20px 0px" }}>
            <Col md={12} className={styles.inputDiv}>
              <label>
                Description <span className={styles.required}>*</span>
                <textarea
                  placeholder="Enter Description"
                  name="description"
                  className={styles.textArea}
                  onChange={handleChange}
                  value={formData.description}
                  disabled={isEditMode}
                />
                {error.description && (
                  <p className={styles.errorMessage}>{error.description}</p>
                )}
              </label>
            </Col>
          </Row>
          {successMessage && (
            <h4
              style={{
                textAlign: "center",
                fontSize: "20px",
                color: "green",
                fontWeight: "bold",
                margin: "20px 0",
              }}
            >
              {successMessage}
            </h4>
          )}

          {error.form && (
            <p style={{ color: "red", textAlign: "center" }}>{error.form}</p>
          )}

          <Row style={{ marginBottom: "20px" }}>
            <Col md={12} className={styles.buttonContainer}>
              <div className={styles.centeredButtons}>
                <Button
                  onClick={handleSubmit}
                  disabled={loader || successMessage !== ""}
                  loading={loader}
                  title={isEditMode ? "Update" : "Submit"}
                />
                <Button title={"Cancel"} onClick={onCancel} disabled={loader} />
              </div>
            </Col>
          </Row>
        </form>
      </div>
    </div>
  );
};
